import firebase from "firebase/app";
import "firebase/storage";
import { getUserId } from "src/config";
import firestore from "src/apiService/firestore";
import type { ExpenseType } from "src/interfaces/systemState.interface";
import type {
  QualifyingQuestions,
  UserSetupQuestions,
} from "src/interfaces/onboarding.interface";
import { recordEventWithCheck } from "src/apiService/modules/events";
import type { SetupCardCompletionSource } from "src/interfaces/event";
import FieldValue = firebase.firestore.FieldValue;

export const acceptUserTerms = () => {
  const acceptUserTerms = firebase.functions().httpsCallable("updateUsersTOS");
  return getUserId().then((userId) => {
    return acceptUserTerms({ userId: userId })
      .then((result) => {
        return true;
      })
      .catch((err) => {
        console.log(err);
        return false;
      });
  });
};

export async function increaseEmptyTablesCounter() {
  const userId = await getUserId();
  const db = firestore();
  await db
    .collection("Users")
    .doc(userId)
    .update({ emptyTablesCount: firebase.firestore.FieldValue.increment(1) });
}

export async function updateScheduleCMappings(
  scheduleCMappings: Record<string, ExpenseType>, // acountName = expense type
  scheduleCGroupings: Record<string, string>, // accountName = group name
  {
    replace = false,
    generalLedgerAccounts,
  }: {
    replace?: boolean;
    generalLedgerAccounts?: { id: string; name: string }[];
  } = {}
) {
  const userId = await getUserId();
  const db = firestore();
  if (replace) {
    await db
      .collection("Users")
      .doc(userId)
      .update({ scheduleCMappings, scheduleCGroupings });
  } else {
    await db.runTransaction(async (t) => {
      const doc = db.collection("Users").doc(userId);
      const user = await t.get(doc);
      const mappings = {
        ...user.data()?.scheduleCMappings,
        ...scheduleCMappings,
      };

      for (const [key, value] of Object.entries(mappings)) {
        if (!value) delete mappings[key];
      }

      const groupings = {
        ...user.data()?.scheduleCGroupings,
        ...scheduleCGroupings,
      };

      for (const [key, value] of Object.entries(groupings)) {
        if (!value || mappings[key] !== "other-expenses") delete groupings[key];
      }

      if (generalLedgerAccounts) {
        for (const accountId of Object.keys(scheduleCMappings)) {
          const account = generalLedgerAccounts.find((a) => a.id === accountId);
          if (account) delete mappings[account.name];
        }

        for (const accountId of Object.keys(scheduleCGroupings)) {
          const account = generalLedgerAccounts.find((a) => a.id === accountId);
          if (account) delete groupings[account.name];
        }
      }

      await t.update(doc, {
        scheduleCMappings: mappings,
        scheduleCGroupings: groupings,
      });
    });
  }
}

export async function doesUserHaveCardPlatform(
  platform: string
): Promise<boolean> {
  const userId = await getUserId();
  const db = firestore();
  const doc = db.collection("Users").doc(userId);
  const userDoc = await doc.get();
  const user = userDoc.data();
  if (!user) return false;

  return !!user.setupQuestions?.sellingPlatforms?.includes(platform);
}

export async function updateUserInitialQuestionnaire(
  qualifyingQuestions: QualifyingQuestions
) {
  const userId = await getUserId();
  const db = firestore();
  await db.collection("Users").doc(userId).update({ qualifyingQuestions });
}

export async function updateUserSetupQuestions(
  setupQuestions: UserSetupQuestions
) {
  const userId = await getUserId();
  const db = firestore();
  await db.collection("Users").doc(userId).update({ setupQuestions });
}

/**
 * Closes out the inventory tracking modal. `declinedInventoryImport` is written
 * in the same transaction so the answer and the modal's completion can never
 * disagree, which is what makes the manual upload card survive a reload.
 */
export async function completeUserInventoryTypeModal({
  declinedInventoryImport = false,
}: { declinedInventoryImport?: boolean } = {}) {
  const userId = await getUserId();
  const db = firestore();
  await db.runTransaction(async (t) => {
    const doc = db.collection("Users").doc(userId);
    const user = await t.get(doc);
    const setupQuestions = user.data()?.setupQuestions || {};

    t.update(doc, {
      setupQuestions: {
        ...setupQuestions,
        inventoryTypeSet: true,
        declinedInventoryImport,
      },
    });
  });
}

interface CompleteSetupCardOptions {
  source?: SetupCardCompletionSource
 }

export async function completeUserSetupQuestionCardPlatform(
  platform: string,
  { source = "flow" }: CompleteSetupCardOptions = {}
) {
  const userId = await getUserId();
  const db = firestore();
  let isFirstPlatform = false;
  await db.runTransaction(async (t) => {
    const doc = db.collection("Users").doc(userId);
    const user = await t.get(doc);
    const setupQuestions = user.data()?.setupQuestions || {};
    const sellingPlatforms = setupQuestions.sellingPlatforms || [];
    const finishedCards = setupQuestions.finishedCards || {};

    isFirstPlatform = !finishedCards.sellingPlatforms?.length;

    const updatedSellingPlatforms = sellingPlatforms.filter(
      (f) => f !== platform
    );
    if (finishedCards.sellingPlatforms) {
      // Guard against a double push: auto-detected completions can fire from
      // more than one render before the snapshot lands, and a duplicate here
      // renders the card twice and double-counts its points.
      if (!finishedCards.sellingPlatforms.includes(platform)) {
        finishedCards.sellingPlatforms.push(platform);
      }
    } else {
      finishedCards.sellingPlatforms = [platform];
    }

    t.update(doc, {
      setupQuestions: {
        ...setupQuestions,
        sellingPlatforms: updatedSellingPlatforms,
        finishedCards,
      },
    });
  });
  recordEventWithCheck({
    type: "feature-adoption",
    feature: "setup-card-completed",
    card: platform,
    source,
  });
  if (isFirstPlatform) {
    recordEventWithCheck({
      type: "feature-adoption",
      feature: "first-platform-connected",
      platform,
      source,
    });
  }
}

export async function completeUserSetupQuestionCardBank({
  source = "flow",
}: CompleteSetupCardOptions = {}) {
  const userId = await getUserId();
  const db = firestore();
  await db.runTransaction(async (t) => {
    const doc = db.collection("Users").doc(userId);
    const user = await t.get(doc);
    const setupQuestions = user.data()?.setupQuestions || {};
    const finishedCards = setupQuestions.finishedCards || {};
    finishedCards.connectBank = true;

    t.update(doc, {
      setupQuestions: {
        ...setupQuestions,
        connectBank: false,
        finishedCards,
      },
    });
  });
  recordEventWithCheck({
    type: "feature-adoption",
    feature: "setup-card-completed",
    card: "connect-bank",
    source,
  });
}

export async function completeUserSetupQuestionCardConnectPlatforms({
  source = "flow",
}: CompleteSetupCardOptions = {}) {
  const userId = await getUserId();
  const db = firestore();
  await db.runTransaction(async (t) => {
    const doc = db.collection("Users").doc(userId);
    const user = await t.get(doc);
    const setupQuestions = user.data()?.setupQuestions || {};
    const sellingPlatforms = setupQuestions.sellingPlatforms || [];
    const finishedCards = setupQuestions.finishedCards || {};

    if (finishedCards.sellingPlatforms) {
      for (const p of sellingPlatforms) {
        if (!finishedCards.sellingPlatforms.includes(p)) {
          finishedCards.sellingPlatforms.push(p);
        }
      }
    } else {
      finishedCards.sellingPlatforms = [...sellingPlatforms];
    }
    finishedCards.connectPlatforms = true;

    t.update(doc, {
      setupQuestions: {
        ...setupQuestions,
        sellingPlatforms: [],
        finishedCards,
      },
    });
  });
  recordEventWithCheck({
    type: "feature-adoption",
    feature: "setup-card-completed",
    card: "connect-platforms",
    source,
  });
}

export async function completeUserSetupQuestionCardManualUpload({
  source = "flow",
}: CompleteSetupCardOptions = {}) {
  const userId = await getUserId();
  const db = firestore();
  await db.runTransaction(async (t) => {
    const doc = db.collection("Users").doc(userId);
    const user = await t.get(doc);
    const setupQuestions = user.data()?.setupQuestions || {};
    const finishedCards = setupQuestions.finishedCards || {};
    finishedCards.manualUpload = true;

    t.update(doc, {
      setupQuestions: {
        ...setupQuestions,
        finishedCards,
      },
    });
  });
  recordEventWithCheck({
    type: "feature-adoption",
    feature: "setup-card-completed",
    card: "manual-upload",
    source,
  });
}

export async function completeUserOnboarding() {
  const userId = await getUserId();
  const db = firestore();
  await db
    .collection("Users")
    .doc(userId)
    .update({ getStartedStep: "complete-set-up" });
}

export async function resetUserOnboarding() {
  const userId = await getUserId();
  const db = firestore();
  await db.collection("Users").doc(userId).update({
    getStartedStep: FieldValue.delete(),
    setupQuestions: FieldValue.delete(),
    qualifyingQuestions: FieldValue.delete(),
  });
}

export async function recordUserActivated(): Promise<boolean> {
  const userId = await getUserId();
  const db = firestore();
  const doc = db.collection("Users").doc(userId);
  const user = await doc.get();
  const data = user.data();
  if (data?.userActivated || !data?.setupQuestions) return false;

  await doc.update({ userActivated: true });
  recordEventWithCheck({
    type: "feature-adoption",
    feature: "user-activated",
  });
  return true;
}
